import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { GameView, MoveView, Tile } from "@word-duel/shared";
import { PRODUCT } from "@word-duel/shared/config";
import { GameBoard } from "../components/GameBoard";
import { HistoryPanel } from "../components/HistoryPanel";
import { Rack } from "../components/Rack";
import { SelfiePanel } from "../components/SelfiePanel";
import {
  TurnAlertsControl,
  useTurnAlerts,
  type TurnAlertsController
} from "../components/TurnAlertsControl";
import { Alert, AppShell, Button, ConfirmDialog, Modal, Spinner } from "../components/ui";
import { api, ApiError, type ActionResponse } from "../lib/api";
import { completionLabel, copyText, formatCountdown, formatRelative } from "../lib/format";
import {
  estimatePlacementScore,
  placementAt,
  repositionPendingTile,
  shuffleTiles,
  tileDisplayLetter,
  tileDisplayName,
  type PendingPlacement
} from "../lib/game";
import {
  getGameSnapshot,
  getStoredGame,
  isGameCode,
  makeInviteUrl,
  makeResumeUrl,
  normalizeGameCode,
  removeStoredGame,
  saveGameSnapshot,
  saveStoredGame,
  type StoredGame
} from "../lib/storage";

type TerminalError = "authorization" | "not-found" | null;

type TileDragSource =
  { origin: "rack"; tile: Tile } | { origin: "board"; tile: Tile; row: number; col: number };

type TileDropTarget = { kind: "board"; row: number; col: number } | { kind: "rack" };

interface TileDragVisual {
  source: TileDragSource;
  clientX: number;
  clientY: number;
  target: TileDropTarget | null;
  valid: boolean;
}

interface TileDragSession {
  pointerId: number;
  startX: number;
  startY: number;
  source: TileDragSource;
  active: boolean;
  target: TileDropTarget | null;
}

function tileDropTargetAt(clientX: number, clientY: number): TileDropTarget | null {
  const element = document.elementFromPoint(clientX, clientY);
  const boardCell = element?.closest<HTMLElement>("[data-board-row][data-board-col]");
  if (boardCell) {
    const row = Number(boardCell.dataset.boardRow);
    const col = Number(boardCell.dataset.boardCol);
    if (Number.isInteger(row) && Number.isInteger(col)) return { kind: "board", row, col };
  }
  if (element?.closest<HTMLElement>('[data-rack-dropzone="true"]')) return { kind: "rack" };
  return null;
}

function scrollBoardNearPointer(clientX: number, clientY: number): void {
  const pointedElement = document.elementFromPoint(clientX, clientY);
  if (pointedElement?.closest<HTMLElement>('[data-rack-dropzone="true"]')) return;
  const viewport = document.querySelector<HTMLElement>('[data-board-viewport="true"]');
  if (!viewport) return;
  const bounds = viewport.getBoundingClientRect();
  if (
    clientX < bounds.left ||
    clientX > bounds.right ||
    clientY < bounds.top ||
    clientY > bounds.bottom
  )
    return;

  const edge = Math.min(52, bounds.width / 4, bounds.height / 4);
  const horizontal = clientX < bounds.left + edge ? -16 : clientX > bounds.right - edge ? 16 : 0;
  const vertical = clientY < bounds.top + edge ? -16 : clientY > bounds.bottom - edge ? 16 : 0;
  if (horizontal || vertical) viewport.scrollBy(horizontal, vertical);
}

function useTileDrag({
  enabled,
  canDrop,
  onDrop,
  onInvalidDrop
}: {
  enabled: boolean;
  canDrop: (source: TileDragSource, target: TileDropTarget) => boolean;
  onDrop: (source: TileDragSource, target: TileDropTarget) => void;
  onInvalidDrop: () => void;
}) {
  const sessionRef = useRef<TileDragSession | null>(null);
  const suppressClickRef = useRef(false);
  const callbacksRef = useRef({ canDrop, onDrop, onInvalidDrop });
  const [drag, setDrag] = useState<TileDragVisual | null>(null);

  useEffect(() => {
    callbacksRef.current = { canDrop, onDrop, onInvalidDrop };
  }, [canDrop, onDrop, onInvalidDrop]);

  const cancelDrag = useCallback(() => {
    sessionRef.current = null;
    setDrag(null);
    document.documentElement.classList.remove("is-tile-dragging");
  }, []);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;
      if (!session.active) {
        const distance = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
        if (distance < 8) return;
        session.active = true;
        document.documentElement.classList.add("is-tile-dragging");
      }

      event.preventDefault();
      scrollBoardNearPointer(event.clientX, event.clientY);
      const target = tileDropTargetAt(event.clientX, event.clientY);
      const valid = target ? callbacksRef.current.canDrop(session.source, target) : false;
      session.target = target;
      setDrag({
        source: session.source,
        clientX: event.clientX,
        clientY: event.clientY,
        target,
        valid
      });
    };

    const onPointerUp = (event: PointerEvent) => {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;
      if (session.active) {
        event.preventDefault();
        const target = tileDropTargetAt(event.clientX, event.clientY) ?? session.target;
        if (target && callbacksRef.current.canDrop(session.source, target)) {
          callbacksRef.current.onDrop(session.source, target);
        } else if (target) {
          callbacksRef.current.onInvalidDrop();
        }
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
      }
      cancelDrag();
    };

    const onPointerCancel = (event: PointerEvent) => {
      if (sessionRef.current?.pointerId === event.pointerId) cancelDrag();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && sessionRef.current?.active) cancelDrag();
    };

    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp, { passive: false });
    window.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", cancelDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", cancelDrag);
      document.documentElement.classList.remove("is-tile-dragging");
    };
  }, [cancelDrag]);

  const startDrag = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>, source: TileDragSource) => {
      if (!enabled || !event.isPrimary || event.button !== 0 || sessionRef.current) return;
      sessionRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        source,
        active: false,
        target: null
      };
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Global pointer listeners still complete the drag when capture is unavailable.
      }
    },
    [enabled]
  );

  const suppressDragClick = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  }, []);

  return { drag, startDrag, suppressDragClick };
}

function useClock(active: boolean): number {
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (!active) return;
    const tick = () => setNow(Date.now());
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

function useGameState(code: string, credentials: StoredGame | null) {
  const cached = useMemo(() => getGameSnapshot(code), [code]);
  const [game, setGame] = useState<GameView | null>(() => cached?.game ?? null);
  const [loading, setLoading] = useState(() => !cached);
  const [refreshing, setRefreshing] = useState(false);
  const [stale, setStale] = useState(() => Boolean(cached));
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [lastSuccessfulAt, setLastSuccessfulAt] = useState<string | null>(
    () => cached?.savedAt ?? null
  );
  const [terminalError, setTerminalError] = useState<TerminalError>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [turnNotice, setTurnNotice] = useState<string | null>(null);
  const failureCount = useRef(0);
  const gameRef = useRef<GameView | null>(cached?.game ?? null);
  const codeRef = useRef(code);

  useEffect(() => {
    if (codeRef.current === code) return;
    codeRef.current = code;
    gameRef.current = cached?.game ?? null;
    failureCount.current = 0;
    setGame(cached?.game ?? null);
    setLoading(!cached);
    setRefreshing(false);
    setStale(Boolean(cached));
    setTerminalError(null);
    setMessage(null);
    setTurnNotice(null);
    setLastSuccessfulAt(cached?.savedAt ?? null);
  }, [cached, code]);

  const acceptGame = useCallback(
    (next: GameView) => {
      const previous = gameRef.current;
      if (previous?.code === next.code && next.revision < previous.revision) return;
      const rivalJustPlayed = Boolean(
        previous &&
          previous.code === next.code &&
          next.revision > previous.revision &&
          next.status === "active" &&
          next.currentSeat === next.me.seat &&
          (previous.status === "waiting" || previous.currentSeat !== previous.me.seat)
      );
      gameRef.current = next;
      setGame(next);
      setStale(false);
      setOffline(false);
      setTerminalError(null);
      setMessage(null);
      if (rivalJustPlayed) {
        setTurnNotice(`${next.opponent?.nickname ?? "Your rival"} played. It’s your turn.`);
      } else if (next.status !== "active" || next.currentSeat !== next.me.seat) {
        setTurnNotice(null);
      }
      const refreshedAt = new Date().toISOString();
      setLastSuccessfulAt(refreshedAt);
      saveGameSnapshot(next);
      if (credentials) {
        saveStoredGame({
          code: next.code,
          token: credentials.token,
          seat: next.me.seat,
          nickname: next.me.nickname,
          createdAt: next.createdAt
        });
      }
    },
    [credentials]
  );

  const refresh = useCallback(
    async (manual = false): Promise<boolean> => {
      if (!credentials) {
        setLoading(false);
        setTerminalError("authorization");
        return false;
      }
      if (manual) setRefreshing(true);
      try {
        const next = await api.getGame(code, credentials.token);
        acceptGame(next);
        failureCount.current = 0;
        return true;
      } catch (caught) {
        failureCount.current += 1;
        if (caught instanceof ApiError) {
          if (caught.status === 401 || caught.status === 403) {
            setTerminalError("authorization");
            setMessage(caught.message);
          } else if (caught.status === 404 || caught.status === 410) {
            setTerminalError("not-found");
            setMessage(caught.message);
          } else {
            setMessage(caught.message);
            setStale(true);
            if (caught.status === 0) setOffline(true);
          }
        } else {
          setMessage("Could not refresh the game.");
          setStale(true);
        }
        return false;
      } finally {
        setLoading(false);
        if (manual) setRefreshing(false);
      }
    },
    [acceptGame, code, credentials]
  );

  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    const poll = async () => {
      if (stopped) return;
      if (document.visibilityState === "visible") await refresh(false);
      if (stopped) return;
      const delay = Math.min(240_000, 30_000 * 2 ** Math.min(failureCount.current, 3));
      timer = window.setTimeout(() => void poll(), delay);
    };
    void poll();
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [refresh]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onServiceWorkerMessage = (event: MessageEvent<unknown>) => {
      if (!event.data || typeof event.data !== "object") return;
      const update = event.data as { type?: unknown; code?: unknown; revision?: unknown };
      if (
        update.type !== "WORD_DUEL_GAME_UPDATED" ||
        update.code !== code ||
        typeof update.revision !== "number" ||
        update.revision <=
          (gameRef.current?.code === code ? gameRef.current.revision : -1)
      )
        return;
      void refresh(false);
    };
    navigator.serviceWorker.addEventListener("message", onServiceWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onServiceWorkerMessage);
  }, [code, refresh]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") void refresh(false);
    };
    const onOnline = () => {
      setOffline(false);
      void refresh(false);
    };
    const onOffline = () => {
      setOffline(true);
      setStale(true);
    };
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refresh]);

  return {
    game,
    setGame: acceptGame,
    loading,
    refreshing,
    stale,
    offline,
    lastSuccessfulAt,
    terminalError,
    message,
    setMessage,
    turnNotice,
    dismissTurnNotice: () => setTurnNotice(null),
    refresh
  };
}

function BlankPicker({
  onPick,
  onClose
}: {
  onPick: (letter: string) => void;
  onClose: () => void;
}) {
  return (
    <Modal
      title="Choose the blank letter"
      description="The blank stays this letter for the rest of the game and is always worth zero."
      onClose={onClose}
    >
      <div className="letter-picker" data-testid="blank-picker">
        {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter) => (
          <button
            key={letter}
            type="button"
            onClick={() => onPick(letter)}
            data-testid={`blank-${letter}`}
          >
            {letter}
          </button>
        ))}
      </div>
    </Modal>
  );
}

function ExchangeDialog({
  rack,
  bagCount,
  busy,
  onExchange,
  onClose
}: {
  rack: Tile[];
  bagCount: number;
  busy: boolean;
  onExchange: (tileIds: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (selected.size > 0) void onExchange([...selected]);
  };
  return (
    <Modal
      title="Exchange tiles"
      description={`Choose 1–7 tiles. Replacements are drawn before these return to the bag. ${bagCount} tiles remain.`}
      onClose={onClose}
    >
      {bagCount < 7 ? (
        <Alert tone="warning">An exchange needs at least seven tiles remaining in the bag.</Alert>
      ) : (
        <form onSubmit={submit}>
          <div className="exchange-rack" data-testid="exchange-rack">
            {rack.map((tile) => {
              const checked = selected.has(tile.id);
              return (
                <label className={`exchange-tile ${checked ? "is-selected" : ""}`} key={tile.id}>
                  <input
                    type="checkbox"
                    aria-label={`${tileDisplayName(tile)}, ${tile.value} points`}
                    checked={checked}
                    onChange={() =>
                      setSelected((current) => {
                        const next = new Set(current);
                        if (next.has(tile.id)) next.delete(tile.id);
                        else next.add(tile.id);
                        return next;
                      })
                    }
                  />
                  <b>{tileDisplayLetter(tile)}</b>
                  <small>{tile.value}</small>
                </label>
              );
            })}
          </div>
          <div className="modal__actions">
            <Button type="button" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={busy || selected.size === 0}
              data-testid="confirm-exchange"
            >
              {busy ? "Exchanging…" : `Exchange ${selected.size || ""}`}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function ErrorState({
  kind,
  message,
  code
}: {
  kind: Exclude<TerminalError, null>;
  message: string | null;
  code: string;
}) {
  const [forgotten, setForgotten] = useState(false);
  const forget = () => {
    removeStoredGame(code);
    setForgotten(true);
  };
  return (
    <AppShell>
      <main className="page page--narrow error-page" data-testid={`${kind}-error`}>
        <p className="eyebrow">
          GAME / {kind === "authorization" ? "PRIVATE ACCESS" : "NOT FOUND"}
        </p>
        <h1>
          {kind === "authorization" ? "Your key doesn’t open this game." : "That game is gone."}
        </h1>
        <p>
          {message ??
            (kind === "authorization"
              ? "Open your personal resume link on this device. An opponent invite does not contain a player key."
              : "The code is invalid, expired, or no longer available.")}
        </p>
        {forgotten && <Alert tone="success">The unusable local entry was removed.</Alert>}
        <div className="button-row">
          <Link className="button button--primary" to="/">
            Back to my games
          </Link>
          {kind === "authorization" && !forgotten && (
            <Button variant="danger" onClick={forget}>
              Forget saved key
            </Button>
          )}
        </div>
      </main>
    </AppShell>
  );
}

function WaitingLobby({
  game,
  credentials,
  stale,
  lastSuccessfulAt,
  refreshing,
  onRefresh,
  turnAlerts
}: {
  game: GameView;
  credentials: StoredGame;
  stale: boolean;
  lastSuccessfulAt: string | null;
  refreshing: boolean;
  onRefresh: () => void;
  turnAlerts: TurnAlertsController;
}) {
  const [notice, setNotice] = useState<string | null>(null);
  const inviteUrl = makeInviteUrl(game.code);
  const resumeUrl = makeResumeUrl(game.code, credentials.token);
  const supportsNativeShare = typeof Reflect.get(navigator, "share") === "function";
  const copy = async (value: string, label: string) =>
    setNotice(
      (await copyText(value)) ? `${label} copied.` : "Copy failed. Select the link manually."
    );
  const share = async () => {
    if (!navigator.share) return copy(inviteUrl, "Invite link");
    try {
      await navigator.share({
        title: `Join my ${PRODUCT.name} game`,
        text: `Your private game code is ${game.code}`,
        url: inviteUrl
      });
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setNotice("Share was unavailable. Copy the invite link instead.");
    }
  };

  return (
    <AppShell compact>
      <main className="waiting-page" data-testid="waiting-lobby">
        <section className="waiting-card">
          <div className="waiting-card__status">
            <span className="pulse-block" aria-hidden="true" />
            <span>Waiting for player two</span>
          </div>
          <p className="eyebrow">GAME CREATED / SEAT ONE SECURED</p>
          <h1>Send the challenge.</h1>
          <p>Your first turn deadline starts only after your rival joins.</p>

          <div className="giant-code" data-testid="game-code">
            <span>PRIVATE GAME CODE</span>
            <strong>{game.code}</strong>
          </div>

          <div className="invite-actions">
            <Button
              variant="primary"
              onClick={() => void copy(inviteUrl, "Invite link")}
              data-testid="copy-invite"
            >
              Copy invite link
            </Button>
            <Button onClick={() => void share()} data-testid="share-invite">
              {supportsNativeShare ? "Share…" : "Copy link"}
            </Button>
          </div>
          {notice && <Alert tone="success">{notice}</Alert>}

          <div className="private-link-box">
            <p className="field__label">Your private resume link</p>
            <p>
              Only this link can restore your seat on another browser. Do not send it to your
              opponent.
            </p>
            <div className="private-link-box__actions">
              <Button
                onClick={() => void copy(resumeUrl, "Private resume link")}
                data-testid="copy-resume"
              >
                Copy private link
              </Button>
              <details>
                <summary>Show link</summary>
                <a href={resumeUrl}>{resumeUrl}</a>
              </details>
            </div>
          </div>

          <TurnAlertsControl controller={turnAlerts} />

          <div className="refresh-line">
            <span className={stale ? "is-stale" : ""}>
              {stale ? "Showing saved state" : "Live"} ·{" "}
              {lastSuccessfulAt
                ? `updated ${formatRelative(lastSuccessfulAt)}`
                : "not yet refreshed"}
            </span>
            <Button
              variant="quiet"
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="manual-refresh"
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </section>
      </main>
    </AppShell>
  );
}

export function GameScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const code = normalizeGameCode(params.code ?? "");
  const validCode = isGameCode(code);
  const credentials = useMemo(() => (validCode ? getStoredGame(code) : null), [code, validCode]);
  const state = useGameState(code, credentials);
  const turnAlerts = useTurnAlerts(code, credentials?.token ?? null);
  const game = state.game;
  const [rack, setRack] = useState<Tile[]>([]);
  const [placements, setPlacements] = useState<PendingPlacement[]>([]);
  const [selectedTileId, setSelectedTileId] = useState<string | null>(null);
  const [blankTarget, setBlankTarget] = useState<{ tile: Tile; row: number; col: number } | null>(
    null
  );
  const [boardZoom, setBoardZoom] = useState(0.92);
  const [confirmAction, setConfirmAction] = useState<"pass" | "resign" | null>(null);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<MoveView[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [gameMenuOpen, setGameMenuOpen] = useState(false);
  const [localSelfieAvailable, setLocalSelfieAvailable] = useState<boolean | null>(null);
  const [rematchBusy, setRematchBusy] = useState(false);
  const [interactionAnnouncement, setInteractionAnnouncement] = useState("");
  const rackRevision = useRef<number | null>(null);
  const now = useClock(Boolean(game?.turnDeadlineAt && game.status === "active"));
  const isMyTurn = Boolean(game && game.status === "active" && game.currentSeat === game.me.seat);
  const canAct = Boolean(isMyTurn && !state.offline && !state.stale && !actionBusy);
  const canResign = Boolean(
    game && game.status === "active" && !state.offline && !state.stale && !actionBusy
  );

  useEffect(() => {
    if (!game || rackRevision.current === game.revision) return;
    rackRevision.current = game.revision;
    setRack((current) => {
      const byId = new Map(game.me.rack.map((tile) => [tile.id, tile]));
      const ordered = current
        .map((tile) => byId.get(tile.id))
        .filter((tile): tile is Tile => Boolean(tile));
      const seen = new Set(ordered.map((tile) => tile.id));
      return [...ordered, ...game.me.rack.filter((tile) => !seen.has(tile.id))];
    });
    setPlacements([]);
    setSelectedTileId(null);
    setLocalSelfieAvailable(null);
  }, [game]);

  const canDropDraggedTile = useCallback(
    (source: TileDragSource, target: TileDropTarget) => {
      if (!game || !canAct) return false;
      const sourcePlacement = placements.find((placement) => placement.tile.id === source.tile.id);
      if (target.kind === "rack") return source.origin === "board" && Boolean(sourcePlacement);
      if (game.board[target.row * 15 + target.col]) return false;

      const targetPlacement = placementAt(placements, target.row, target.col);
      if (source.origin === "rack") {
        return !sourcePlacement && !targetPlacement;
      }
      return Boolean(sourcePlacement);
    },
    [canAct, game, placements]
  );

  const handleDraggedTileDrop = useCallback(
    (source: TileDragSource, target: TileDropTarget) => {
      if (!game || !canAct) return;
      const letter = tileDisplayName(source.tile);
      if (target.kind === "rack") {
        setPlacements((current) =>
          current.filter((placement) => placement.tile.id !== source.tile.id)
        );
        setSelectedTileId(source.tile.id);
        setInteractionAnnouncement(`${letter} returned to your rack.`);
        return;
      }

      if (source.origin === "rack") {
        if (source.tile.value === 0) {
          setBlankTarget({ tile: source.tile, row: target.row, col: target.col });
          setInteractionAnnouncement("Choose a letter for the blank tile.");
          return;
        }
        setPlacements((current) => [
          ...current,
          { tile: source.tile, row: target.row, col: target.col }
        ]);
        setSelectedTileId(null);
        setInteractionAnnouncement(
          `${letter} placed at row ${target.row + 1}, column ${target.col + 1}.`
        );
        return;
      }

      const targetPlacement = placementAt(placements, target.row, target.col);
      setPlacements((current) =>
        repositionPendingTile(current, source.tile.id, target.row, target.col)
      );
      setSelectedTileId(null);
      setInteractionAnnouncement(
        targetPlacement && targetPlacement.tile.id !== source.tile.id
          ? `${letter} swapped with ${tileDisplayName(targetPlacement.tile)} at row ${target.row + 1}, column ${target.col + 1}.`
          : `${letter} moved to row ${target.row + 1}, column ${target.col + 1}.`
      );
    },
    [canAct, game, placements]
  );

  const tileDrag = useTileDrag({
    enabled: canAct,
    canDrop: canDropDraggedTile,
    onDrop: handleDraggedTileDrop,
    onInvalidDrop: () =>
      setInteractionAnnouncement("That square cannot take this tile. The move was unchanged.")
  });

  if (!validCode)
    return (
      <ErrorState
        kind="not-found"
        message="The URL does not contain a valid 10-character game code."
        code={code}
      />
    );
  if (state.terminalError)
    return <ErrorState kind={state.terminalError} message={state.message} code={code} />;
  if (state.loading && !game) {
    return (
      <AppShell compact>
        <main className="loading-page">
          <Spinner label="Opening private game…" />
        </main>
      </AppShell>
    );
  }
  if (!game || !credentials) {
    return (
      <AppShell>
        <main className="page page--narrow error-page" data-testid="offline-empty">
          <p className="eyebrow">OFFLINE / NO SAVED BOARD</p>
          <h1>This game isn’t on this device yet.</h1>
          <p>
            Reconnect to open it, or use the player’s private resume link. Moves are never queued
            while offline.
          </p>
          <Link className="button button--primary" to="/">
            My games
          </Link>
        </main>
      </AppShell>
    );
  }

  if (game.status === "waiting") {
    return (
      <WaitingLobby
        game={game}
        credentials={credentials}
        stale={state.stale || state.offline}
        lastSuccessfulAt={state.lastSuccessfulAt}
        refreshing={state.refreshing}
        onRefresh={() => void state.refresh(true)}
        turnAlerts={turnAlerts}
      />
    );
  }

  const opponent = game.opponent;
  const placedTileIds = new Set(placements.map((placement) => placement.tile.id));
  const estimate = estimatePlacementScore(game.board, placements);
  const countdown = formatCountdown(game.turnDeadlineAt, now);
  const activeName = game.currentSeat === game.me.seat ? game.me.nickname : opponent?.nickname;
  const selfieAvailable = localSelfieAvailable ?? game.selfieAvailable;
  const turnLabel =
    game.status === "active"
      ? isMyTurn
        ? "YOUR TURN"
        : `${activeName?.toUpperCase() ?? "RIVAL"}’S TURN`
      : "FINAL";
  const clockLabel =
    game.status === "active"
      ? (countdown ?? "NO TIMER")
      : game.winningSeat === null
        ? "DRAW"
        : `P${game.winningSeat} WINS`;

  const clearPendingMove = () => {
    setPlacements([]);
    setSelectedTileId(null);
    setInteractionAnnouncement("Pending move cleared.");
  };

  const handleSquare = (row: number, col: number) => {
    const existing = placementAt(placements, row, col);
    if (existing) {
      setPlacements((current) =>
        current.filter((placement) => placement.tile.id !== existing.tile.id)
      );
      setSelectedTileId(existing.tile.id);
      setInteractionAnnouncement(`${tileDisplayName(existing.tile)} returned to your rack.`);
      return;
    }
    if (!canAct || !selectedTileId || game.board[row * 15 + col]) return;
    const tile = rack.find((candidate) => candidate.id === selectedTileId);
    if (!tile || placedTileIds.has(tile.id)) return;
    if (tile.value === 0) {
      setBlankTarget({ tile, row, col });
      setInteractionAnnouncement("Choose a letter for the blank tile.");
      return;
    }
    setPlacements((current) => [...current, { tile, row, col }]);
    setSelectedTileId(null);
    setInteractionAnnouncement(
      `${tileDisplayName(tile)} placed at row ${row + 1}, column ${col + 1}.`
    );
  };

  const applyAction = async (work: () => Promise<ActionResponse>) => {
    setActionBusy(true);
    setActionMessage(null);
    try {
      const result = await work();
      state.setGame(result.game);
      setPlacements([]);
      setSelectedTileId(null);
      setConfirmAction(null);
      setExchangeOpen(false);
    } catch (caught) {
      if (caught instanceof ApiError) {
        const invalidWords =
          caught.details && typeof caught.details === "object" && "invalidWords" in caught.details
            ? (caught.details as { invalidWords?: unknown }).invalidWords
            : null;
        setActionMessage(
          Array.isArray(invalidWords) && invalidWords.length > 0
            ? `Not in the dictionary: ${invalidWords.map(String).join(", ")}. Nothing was played.`
            : caught.status === 409
              ? "The board changed before this action arrived. The latest game has been loaded; review your move."
              : caught.message
        );
        if (caught.status === 409) {
          setPlacements([]);
          setSelectedTileId(null);
          await state.refresh(false);
        }
      } else setActionMessage("The action could not be completed.");
    } finally {
      setActionBusy(false);
    }
  };

  const submitMove = () => {
    if (placements.length === 0) return;
    const payload = placements.map((placement) => ({
      tileId: placement.tile.id,
      row: placement.row,
      col: placement.col,
      ...(placement.blankLetter ? { blankLetter: placement.blankLetter } : {})
    }));
    void applyAction(() =>
      api.place(game.code, credentials.token, {
        revision: game.revision,
        idempotencyKey: crypto.randomUUID(),
        placements: payload
      })
    );
  };

  const runConfirmedAction = async () => {
    if (confirmAction === "pass") {
      await applyAction(() =>
        api.pass(game.code, credentials.token, {
          revision: game.revision,
          idempotencyKey: crypto.randomUUID()
        })
      );
    } else if (confirmAction === "resign") {
      await applyAction(() =>
        api.resign(game.code, credentials.token, {
          revision: game.revision,
          idempotencyKey: crypto.randomUUID()
        })
      );
    }
  };

  const exchange = (tileIds: string[]) =>
    applyAction(() =>
      api.exchange(game.code, credentials.token, {
        revision: game.revision,
        idempotencyKey: crypto.randomUUID(),
        tileIds
      })
    );

  const openHistory = () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    api
      .getMoves(game.code, credentials.token)
      .then(setHistory)
      .catch((caught) =>
        setHistoryError(
          caught instanceof ApiError ? caught.message : "Could not load move history."
        )
      )
      .finally(() => setHistoryLoading(false));
  };

  const startRematch = async () => {
    setRematchBusy(true);
    setActionMessage(null);
    try {
      const result = await api.rematch(game.code, credentials.token);
      saveStoredGame({
        code: result.game.code,
        token: result.token,
        seat: result.seat,
        nickname: result.game.me.nickname,
        createdAt: result.game.createdAt
      });
      navigate(`/game/${result.game.code}`);
    } catch (caught) {
      setActionMessage(
        caught instanceof ApiError ? caught.message : "Could not start the rematch."
      );
    } finally {
      setRematchBusy(false);
    }
  };

  const winnerName = game.winningSeat === game.me.seat ? game.me.nickname : opponent?.nickname;
  const resultForMe =
    game.winningSeat === null ? "draw" : game.winningSeat === game.me.seat ? "win" : "loss";
  const gameHeaderContent = (
    <div className="game-score-header" data-testid="game-score-header">
      <div className={`header-score ${game.currentSeat === game.me.seat ? "is-active" : ""}`}>
        <span>YOU / P{game.me.seat}</span>
        <strong>{game.me.score}</strong>
        <b title={game.me.nickname}>{game.me.nickname}</b>
        <small>{game.me.tileCount} tiles</small>
      </div>
      <details className="game-status-subview" data-testid="game-status-subview">
        <summary>
          <span>{turnLabel}</span>
          <strong
            className={
              game.turnDeadlineAt && new Date(game.turnDeadlineAt).getTime() <= now
                ? "is-overdue"
                : ""
            }
          >
            {clockLabel}
          </strong>
        </summary>
        <div className="game-status-subview__panel">
          <span>
            {game.status === "active"
              ? countdown
                ? "Server deadline"
                : "Play when ready"
              : completionLabel(game.completionReason)}
          </span>
          <Link to="/">My games</Link>
        </div>
      </details>
      {game.status === "active" && (
        <button
          type="button"
          className="mobile-game-menu-button"
          aria-label="Game menu"
          aria-haspopup="dialog"
          aria-expanded={gameMenuOpen}
          aria-controls="game-menu-dialog"
          title={`${turnLabel} · ${clockLabel}`}
          onClick={() => setGameMenuOpen(true)}
          data-testid="game-menu-button"
        >
          <span aria-hidden="true">☰</span>
        </button>
      )}
      <div
        className={`header-score header-score--right ${game.currentSeat === opponent?.seat ? "is-active" : ""}`}
      >
        <span>RIVAL / P{opponent?.seat ?? "—"}</span>
        <strong>{opponent?.score ?? 0}</strong>
        <b title={opponent?.nickname ?? "Waiting"}>{opponent?.nickname ?? "Waiting"}</b>
        <small>{opponent?.tileCount ?? 0} tiles</small>
      </div>
    </div>
  );

  return (
    <AppShell compact playMode={game.status === "active"} headerContent={gameHeaderContent}>
      <main
        className={`game-page game-page--${game.status}`}
        data-testid={game.status === "complete" ? "completed-game" : "game-screen"}
        onClickCapture={tileDrag.suppressDragClick}
      >
        {(state.offline || state.stale || state.message) && (
          <Alert tone={state.offline ? "warning" : state.message ? "error" : "warning"}>
            <div className="stale-alert">
              <span>
                {state.offline
                  ? "Offline: this is the last saved board. Moves are disabled and will not be queued."
                  : (state.message ?? "This board may be out of date.")}{" "}
                {state.lastSuccessfulAt && (
                  <small>Last live update {formatRelative(state.lastSuccessfulAt)}.</small>
                )}
              </span>
              <Button
                variant="quiet"
                onClick={() => void state.refresh(true)}
                disabled={state.refreshing}
                data-testid="manual-refresh"
              >
                {state.refreshing ? "Refreshing…" : "Try again"}
              </Button>
            </div>
          </Alert>
        )}
        {actionMessage && <Alert tone="error">{actionMessage}</Alert>}
        {state.turnNotice && (
          <div className="turn-notice" role="status" data-testid="turn-notice">
            <span>
              <strong>Your turn.</strong> {state.turnNotice}
            </span>
            <button type="button" onClick={state.dismissTurnNotice} aria-label="Dismiss turn alert">
              ×
            </button>
          </div>
        )}

        {game.status === "complete" && (
          <section className={`result-banner result-banner--${resultForMe}`}>
            <div>
              <p className="eyebrow">
                GAME OVER / {completionLabel(game.completionReason).toUpperCase()}
              </p>
              <h1>
                {resultForMe === "draw"
                  ? "Dead even."
                  : resultForMe === "win"
                    ? "Board owned."
                    : "Good fight."}
              </h1>
              <p>
                {resultForMe === "draw"
                  ? `You and ${opponent?.nickname ?? "your rival"} finished level.`
                  : `${winnerName ?? "Player"} wins ${game.me.seat === 1 ? `${game.me.score}—${opponent?.score ?? 0}` : `${opponent?.score ?? 0}—${game.me.score}`}.`}
              </p>
            </div>
            <div className="result-banner__actions">
              <Button
                variant="primary"
                onClick={() => void startRematch()}
                disabled={rematchBusy}
                data-testid="rematch-button"
              >
                {rematchBusy
                  ? "Setting up…"
                  : game.rematchCode
                    ? "Join rematch →"
                    : "Start rematch →"}
              </Button>
              <Button onClick={openHistory} data-testid="history-button">
                Full history
              </Button>
            </div>
            {game.rematchCode && (
              <Alert tone="success">
                A rematch has been requested. Join it to claim your new private seat.
              </Alert>
            )}
          </section>
        )}

        <div className="game-workspace">
          <GameBoard
            board={game.board}
            placements={placements}
            selectedTileId={selectedTileId}
            canPlace={canAct}
            zoom={boardZoom}
            centerKey={game.code}
            dragTarget={
              tileDrag.drag?.target?.kind === "board"
                ? { ...tileDrag.drag.target, valid: tileDrag.drag.valid }
                : null
            }
            draggingTileId={tileDrag.drag?.source.tile.id ?? null}
            bottomDock={
              game.status === "active" ? (
                <div className="turn-dock" data-rack-dropzone="true">
                  <span className="rack-shelf" aria-hidden="true" />
                  <Rack
                    tiles={rack}
                    placedTileIds={placedTileIds}
                    selectedTileId={selectedTileId}
                    draggingTileId={tileDrag.drag?.source.tile.id ?? null}
                    disabled={!canAct}
                    onTilesChange={setRack}
                    onSelect={(tileId) =>
                      setSelectedTileId((current) => (current === tileId ? null : tileId))
                    }
                    onTilePointerDown={(event, tile) =>
                      tileDrag.startDrag(event, { origin: "rack", tile })
                    }
                  />
                  <div className="mobile-board-actions" aria-label="Move actions">
                    <Button
                      variant="quiet"
                      onClick={clearPendingMove}
                      disabled={placements.length === 0 || actionBusy}
                      data-testid="mobile-clear-move"
                    >
                      Clear
                    </Button>
                    <Button
                      variant="primary"
                      onClick={submitMove}
                      disabled={!canAct || placements.length === 0}
                      data-testid="mobile-submit-move"
                    >
                      {actionBusy ? "Playing…" : `Play +${estimate}`}
                    </Button>
                  </div>
                  <div className="turn-quick-actions">
                    <Button
                      variant="primary"
                      onClick={submitMove}
                      disabled={!canAct || placements.length === 0}
                      data-testid="submit-move"
                    >
                      {actionBusy ? "Submitting…" : `Play +${estimate}`}
                    </Button>
                    <details className="turn-options" data-testid="turn-options">
                      <summary className="button button--quiet">Turn options</summary>
                      <div className="turn-options__panel">
                        <div className="turn-options__estimate">
                          <span>EST. TURN</span>
                          <strong>+{estimate}</strong>
                          <small>Server confirms the score</small>
                        </div>
                        <Button
                          onClick={clearPendingMove}
                          disabled={placements.length === 0 || actionBusy}
                          data-testid="clear-move"
                        >
                          Clear
                        </Button>
                        <Button
                          variant="quiet"
                          onClick={() => setExchangeOpen(true)}
                          disabled={!canAct || placements.length > 0 || game.bagCount < 7}
                          data-testid="exchange-button"
                        >
                          Exchange
                        </Button>
                        <Button
                          variant="quiet"
                          onClick={() => setConfirmAction("pass")}
                          disabled={!canAct || placements.length > 0}
                          data-testid="pass-button"
                        >
                          Pass
                        </Button>
                        <Button
                          variant="quiet"
                          className="danger-link"
                          onClick={() => setConfirmAction("resign")}
                          disabled={!canResign || placements.length > 0}
                          data-testid="resign-button"
                        >
                          Resign
                        </Button>
                      </div>
                    </details>
                  </div>
                </div>
              ) : undefined
            }
            onZoom={setBoardZoom}
            onSquare={handleSquare}
            onPendingTilePointerDown={(event, placement) =>
              tileDrag.startDrag(event, {
                origin: "board",
                tile: placement.tile,
                row: placement.row,
                col: placement.col
              })
            }
          />

          <aside className="game-sidebar">
            <div className="game-meta">
              <div>
                <span>GAME</span>
                <strong>{game.code}</strong>
              </div>
              <div>
                <span>BAG</span>
                <strong>{game.bagCount}</strong>
              </div>
              <div>
                <span>REV</span>
                <strong>{game.revision}</strong>
              </div>
              <div>
                <span>SCORELESS</span>
                <strong>{game.consecutiveScorelessTurns}/6</strong>
              </div>
            </div>
            <Button onClick={openHistory} data-testid="history-button">
              View move history
            </Button>
            <TurnAlertsControl controller={turnAlerts} compact />
            <Button
              variant="quiet"
              onClick={() => void state.refresh(true)}
              disabled={state.refreshing}
              data-testid="manual-refresh"
            >
              {state.refreshing ? "Refreshing…" : "Refresh board"}
            </Button>
            <p className="last-refresh">
              {state.lastSuccessfulAt
                ? `Last live update ${formatRelative(state.lastSuccessfulAt)}`
                : "No live update yet"}
            </p>
          </aside>
        </div>

        {game.status === "complete" && game.winningSeat !== null && (
          <SelfiePanel
            code={game.code}
            token={credentials.token}
            isWinner={game.winningSeat === game.me.seat}
            available={selfieAvailable}
            onAvailabilityChange={setLocalSelfieAvailable}
          />
        )}

        {gameMenuOpen && game.status === "active" && (
          <Modal
            id="game-menu-dialog"
            className="game-menu-modal"
            eyebrow="GAME / MENU"
            title="Game menu"
            description={`${turnLabel} · ${clockLabel}`}
            onClose={() => setGameMenuOpen(false)}
          >
            <div className="game-menu" data-testid="game-menu">
              <section className="game-menu__section" aria-labelledby="game-menu-turn">
                <h3 id="game-menu-turn">Turn</h3>
                <div className="game-menu__actions">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setGameMenuOpen(false);
                      submitMove();
                    }}
                    disabled={!canAct || placements.length === 0}
                    data-testid="menu-submit-move"
                  >
                    {actionBusy ? "Submitting…" : `Play +${estimate}`}
                  </Button>
                  <Button
                    onClick={() => {
                      clearPendingMove();
                      setGameMenuOpen(false);
                    }}
                    disabled={placements.length === 0 || actionBusy}
                    data-testid="menu-clear-move"
                  >
                    Clear tiles
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={() => {
                      setRack((current) => shuffleTiles(current));
                      setGameMenuOpen(false);
                    }}
                    disabled={actionBusy}
                    data-testid="menu-shuffle-rack"
                  >
                    Shuffle rack
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={() => {
                      setGameMenuOpen(false);
                      setExchangeOpen(true);
                    }}
                    disabled={!canAct || placements.length > 0 || game.bagCount < 7}
                    data-testid="menu-exchange"
                  >
                    Exchange
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={() => {
                      setGameMenuOpen(false);
                      setConfirmAction("pass");
                    }}
                    disabled={!canAct || placements.length > 0}
                    data-testid="menu-pass"
                  >
                    Pass
                  </Button>
                  <Button
                    variant="quiet"
                    className="danger-link"
                    onClick={() => {
                      setGameMenuOpen(false);
                      setConfirmAction("resign");
                    }}
                    disabled={!canResign || placements.length > 0}
                    data-testid="menu-resign"
                  >
                    Resign
                  </Button>
                </div>
              </section>

              <section className="game-menu__section" aria-labelledby="game-menu-views">
                <h3 id="game-menu-views">Game views</h3>
                <div className="game-menu__actions">
                  <Button
                    onClick={() => {
                      setGameMenuOpen(false);
                      openHistory();
                    }}
                    data-testid="menu-history"
                  >
                    Move history
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={() => {
                      setGameMenuOpen(false);
                      void state.refresh(true);
                    }}
                    disabled={state.refreshing}
                    data-testid="menu-refresh"
                  >
                    {state.refreshing ? "Refreshing…" : "Refresh board"}
                  </Button>
                  <Link
                    className="button button--quiet"
                    to="/"
                    onClick={() => setGameMenuOpen(false)}
                  >
                    My games
                  </Link>
                </div>
              </section>

              <section className="game-menu__section" aria-labelledby="game-menu-alerts">
                <h3 id="game-menu-alerts">Notifications</h3>
                <TurnAlertsControl controller={turnAlerts} compact />
              </section>

              <dl className="game-menu__meta">
                <div>
                  <dt>Game</dt>
                  <dd>{game.code}</dd>
                </div>
                <div>
                  <dt>Bag</dt>
                  <dd>{game.bagCount}</dd>
                </div>
                <div>
                  <dt>Scoreless</dt>
                  <dd>{game.consecutiveScorelessTurns}/6</dd>
                </div>
              </dl>
            </div>
          </Modal>
        )}

        {blankTarget && (
          <BlankPicker
            onPick={(letter) => {
              setPlacements((current) => [...current, { ...blankTarget, blankLetter: letter }]);
              setSelectedTileId(null);
              setBlankTarget(null);
              setInteractionAnnouncement(
                `${letter} blank placed at row ${blankTarget.row + 1}, column ${blankTarget.col + 1}.`
              );
            }}
            onClose={() => setBlankTarget(null)}
          />
        )}
        {exchangeOpen && (
          <ExchangeDialog
            rack={rack}
            bagCount={game.bagCount}
            busy={actionBusy}
            onExchange={exchange}
            onClose={() => setExchangeOpen(false)}
          />
        )}
        {confirmAction && (
          <ConfirmDialog
            title={confirmAction === "pass" ? "Pass this turn?" : "Resign this game?"}
            description={
              confirmAction === "pass"
                ? "Passing scores zero and counts toward six consecutive scoreless turns."
                : `Resigning ends the game immediately and awards ${opponent?.nickname ?? "your rival"} the win.`
            }
            confirmLabel={confirmAction === "pass" ? "Pass" : "Resign"}
            danger={confirmAction === "resign"}
            busy={actionBusy}
            onConfirm={runConfirmedAction}
            onClose={() => setConfirmAction(null)}
          />
        )}
        {historyOpen && (
          <HistoryPanel
            moves={history}
            loading={historyLoading}
            error={historyError}
            mySeat={game.me.seat}
            onClose={() => setHistoryOpen(false)}
          />
        )}
        {tileDrag.drag && (
          <div
            className={`tile-drag-ghost ${tileDrag.drag.target ? (tileDrag.drag.valid ? "is-valid" : "is-invalid") : ""}`}
            style={{ left: tileDrag.drag.clientX, top: tileDrag.drag.clientY }}
            aria-hidden="true"
            data-testid="tile-drag-ghost"
          >
            <b>{tileDisplayLetter(tileDrag.drag.source.tile)}</b>
            <small>{tileDrag.drag.source.tile.value}</small>
          </div>
        )}
        <p className="sr-only" role="status" aria-live="polite">
          {interactionAnnouncement}
        </p>
      </main>
    </AppShell>
  );
}
